/*
 * Parent or carer details on the pupil record (TDI-622, R-005): the SENCO's
 * "Change parent or carer" form.
 *
 * Pure functions only, shared by HarbourChangeParentModal and its tests.
 */

// The relationships Add a pupil offers ("Relationship to {child}"), in the
// same order. The server accepts these and "Informal carer", which older
// referral forms recorded; a parent already recorded that way keeps it.
export const PARENT_RELATIONSHIPS = [
  { value: 'parent', label: 'Parent' },
  { value: 'step_parent', label: 'Step-parent' },
  { value: 'foster_carer', label: 'Foster carer' },
  { value: 'kinship_carer', label: 'Kinship carer' },
  { value: 'legal_guardian', label: 'Legal guardian' },
];

const EXTRA_RELATIONSHIPS = { informal_carer: 'Informal carer' };

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** The options for one parent: the standard list, plus their own if it is not on it. */
export function relationshipOptionsFor(current) {
  if (!current || PARENT_RELATIONSHIPS.some((r) => r.value === current)) return PARENT_RELATIONSHIPS;
  return [...PARENT_RELATIONSHIPS, { value: current, label: EXTRA_RELATIONSHIPS[current] || current }];
}

export function parentFullName(parent) {
  return [parent && parent.firstName, parent && parent.lastName].filter(Boolean).join(' ').trim();
}

/** The parent or carer has signed in: from then on the email is theirs to change, with pilot support. */
export function parentHasSignedIn(parent) {
  return Boolean(parent) && parent.status === 'account_linked';
}

/**
 * Whether the family invitation has gone to this address, so changing it
 * stops a link they may already have (the change asks first).
 */
export function parentWasInvited(parent) {
  return Boolean(parent) && (parent.status === 'invite_sent' || Boolean(parent.invitation));
}

/**
 * The form's errors, keyed by field. Empty object when it can be sent. The
 * email is only checked while it can be changed.
 *
 * @param {{ firstName: string, lastName: string, email: string }} form
 * @param {{ emailLocked?: boolean }} options
 */
export function parentDetailsErrors(form, { emailLocked = false } = {}) {
  const errors = {};
  const email = String(form.email || '').trim();
  if (!String(form.firstName || '').trim()) errors.firstName = 'Enter the parent or carer’s first name';
  if (!String(form.lastName || '').trim()) errors.lastName = 'Enter the parent or carer’s last name';
  if (!emailLocked) {
    if (!email) errors.email = 'Enter the parent or carer’s email address';
    else if (!EMAIL_PATTERN.test(email)) errors.email = 'Enter an email address in the correct format, like name@example.com';
  }
  return errors;
}

/** Whether the email in the form differs from the one on the record (case and spaces ignored). */
export function parentEmailChanged(form, parent) {
  return String(form.email || '').trim().toLowerCase()
    !== String((parent && parent.email) || '').trim().toLowerCase();
}
